import { useEffect } from "react"
import { useFormContext } from "react-hook-form"

import { FieldErrorType } from "@/types/common"

interface UseDuplicateByFieldValidationProps<T> {
  query: string
  list: T[]
  fieldName: Extract<keyof T, string>
  fieldLabel: string
  entityName: string
  getFieldValue?: (item: T) => string
}

const useDuplicateByFieldValidation = <T,>({
  query,
  list,
  fieldName,
  fieldLabel,
  entityName,
  getFieldValue,
}: UseDuplicateByFieldValidationProps<T>): void => {
  const {
    setError,
    clearErrors,
    formState: { dirtyFields, errors },
  } = useFormContext()

  useEffect(() => {
    if (query && (dirtyFields[fieldName] as string)) {
      const normalizedQuery = query.toLowerCase().trim()

      const isDuplicate = list.some((item) => {
        const normalizedItemFieldValue = (getFieldValue ? getFieldValue(item) : ((item[fieldName] ?? "") as string))
          .toLowerCase()
          .trim()

        return normalizedItemFieldValue === normalizedQuery
      })

      if (isDuplicate) {
        setError(fieldName, {
          type: FieldErrorType.Duplicate,
          message: `${entityName} with this ${fieldLabel} already exists.`,
        })
      } else if (errors[fieldName]?.type === FieldErrorType.Duplicate) {
        clearErrors(fieldName)
      }
    }
  }, [query, list, fieldName, fieldLabel, setError, clearErrors, getFieldValue, dirtyFields, errors, entityName])
}

export default useDuplicateByFieldValidation
